import { defineStore } from 'pinia'
import Api from '../api/Api'

function sortLabels(a, b) {
    if (a.start < b.start) return -1;
    if (a.start > b.start) return 1;
    return 0;
}

function fixTimeFormats(label) {

  if (!label.start && label.start !== 0) {
    return label;
  }

  label.start /= 1000;
  label.end /= 1000;
  let duration = label.end - label.start;
  label.duration = Number(duration.toFixed(1));

  if (label.content.length > 0) {
    label.charsPerSec = (label.content.length / duration).toFixed(1);
  } else {
    label.charsPerSec = '-';
  }

  return label;
}

export const usePodLabelStore = defineStore('podLabel', {
  state: () => ({
    labels: [],
    mllabels: [],
  }),
  getters: {
    getLabels: function(state) {
      return state.labels;
    },
    getMlLabels: function(state) {
      return state.mllabels;
    },
    /**
     * Cmobine the AWS detected labels into a single label
     * @param state
     * @returns {*[]}
     * @deprecated The labels are now combined in the backend
     */
    getCombinedAWSDetectedLabels: function(state) {
      let awsDetectedLabels = state.mllabels.filter(x => x.service === 'aws-detecting-labels');
      let labelsByContent = {};
      let response = [];

      // separate the labels by content
      for (let i = 0; i < awsDetectedLabels.length; i++) {
        const content = awsDetectedLabels[i].content;
        if (!labelsByContent[content]) {
          labelsByContent[content] = [];
        }

        let labelCloned = JSON.parse(JSON.stringify(awsDetectedLabels[i]));
        labelsByContent[content].push(labelCloned);
      }

      // remove some similar labels
      for (let content in labelsByContent) {
        if (Object.prototype.hasOwnProperty.call(labelsByContent, content)) {
          let similarLabels = labelsByContent[content];

          for (let i = 0; i < similarLabels.length; i++) {
            if  (similarLabels[i+1] &&
                (similarLabels[i+1].start >= similarLabels[i].end) &&
                (similarLabels[i+1].start <= similarLabels[i].end + 1) )
            {
              labelsByContent[content][i].end = similarLabels[i+1].end;
              labelsByContent[content][i].duration = labelsByContent[content][i+1].end - labelsByContent[content][i].start;
              labelsByContent[content].splice(i+1, 1);
              i = -1;
            }
          }
        }
      }

      // merge the labels back
      for (let content in labelsByContent) {
        if (Object.prototype.hasOwnProperty.call(labelsByContent, content)) {
          response = response.concat(labelsByContent[content]);
        }
      }

      response.sort(sortLabels);
      return response;
    }
  },
  actions: {
    _addLabel: function(label) {
      this.labels.push(fixTimeFormats(label));
      this.labels.sort(sortLabels);
    },
    _updateMlLabel: function(label) {
      for (let i = 0; i < this.mllabels.length; i++) {
        if (this.mllabels[i].id == label.id) {
          let fixedLabel = fixTimeFormats(label);
          Object.assign(this.mllabels[i], fixedLabel);
          break;
        }
      }

      this.mllabels.sort(sortLabels);
    },
    _deleteMlLabel: function(labelID) {
      for (let i = 0; i < this.mllabels.length; i++) {
        if (this.mllabels[i].id == labelID) {
          this.mllabels.splice(i, 1);
          break;
        }
      }
    },


    refreshLabels: async function(podID) {
      const response = await Api.podlabel.getLabels(podID);
      this.labels = response.data.map(fixTimeFormats);
      this.labels.sort(sortLabels);

      return response.data;
    },

    createLabel: async function(data) {
      const response = await Api.podlabel.createLabel(data.podID, data.payload);
      this._addLabel(response.data);
      return response.data;
    },

    /**
     * @param {{
     *   podids: string[],
     *   labels: string[]
     * }} data
     * @returns {Promise<void>}
     */
    createLabels: async function(data) {
      const response = await Api.podlabel.createManyLabels(data);
      for (const label of response.data) {
        this._addLabel(label);
      }
    },

    /**
     * Create labels for multiple pods and nodes
     * @param {{
     *   podIds: string[],
     *   nodeIds?: string[],
     *   labels: string[]
     * }} data
     *
     * @param data
     * @returns {Promise<void>}
     */
    createLabelsForPodsAndNodes: async function(data) {
      const response = await Api.podlabel.createLabelsForPodsAndNodes(data);
      for (const label of response.data) {
        this._addLabel(label);
      }
    },

    updateLabel: async function(data) {
      const response = await Api.podlabel.updateLabel(data.podID, data.labelID, data.payload);

      for (let i = 0; i < this.labels.length; i++) {
        if (this.labels[i].id == response.data.id) {
          let fixedLabel = fixTimeFormats(response.data);
          Object.assign(this.labels[i], fixedLabel);
          break;
        }
      }

      this.labels.sort(sortLabels);

      return response.data;
    },

    deleteLabel: async function(label) {
      await Api.podlabel.deleteLabel(label.podID, label.labelID);

      for (let i = 0; i < this.labels.length; i++) {
        if (this.labels[i].id == label.labelID) {
          this.labels.splice(i, 1);
          break;
        }
      }
    },

    refreshMlLabels: async function(podID) {
      const response = await Api.podlabel.getMlLabels(podID);

      this.mllabels = response.data.map(fixTimeFormats);;
      this.mllabels.sort(sortLabels);

      return response.data;
    },

    updateMlLabel: async function(data) {
      const response = await Api.podlabel.updateMlLabel(data.podID, data.labelID, data.payload);
      this._updateMlLabel(response.data);
      return response.data;
    },

    deleteMlLabel: async function(label) {
      await Api.podlabel.deleteMlLabel(label.podID, label.labelID);
      this._deleteMlLabel(label.labelID);
    },

    updateMlText: async function(data) {
      const response = await Api.podlabel.updateMlText(data.podID, data.labelID, data.payload);
      this._updateMlLabel(response.data);
      return response.data;
    },

    deleteMlText: async function(label) {
      await Api.podlabel.deleteMlText(label.podID, label.labelID);
      this._deleteMlLabel(label.labelID);
    },
  },
});
